import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import {
  FAQS,
  FOOTER_LINKS,
  MARKETPLACE_FAQS,
  BUYER_HOW_IT_WORKS,
  REGISTRATION_CARDS,
  SELLER_HOW_IT_WORKS,
  TERMS_AND_CONDITION,
  CreateListingTermsAndCondition,
  ADD_ON_PURCHASE_SUCCESS_MESSAGE,
  ListingCheckoutTermsAndCondition,
  MARKETPLACE_EVENTS_PAGE_INFO_TEXT,
  MARKETPLACE_CREATE_LISTING_PAGE_INFO_TEXT,
} from "./helper";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

import GroupInviteBanner from "public/images/oregon/ducks-group.jpg";

const LOGO_URL =
  "https://ss-leagues.s3.amazonaws.com/NCAA/Oregon_Ducks_logo_green.png";
const OrderSummaryDesktopImage =
  "https://ss-leagues.s3.amazonaws.com/NCAA/oregon-bg.png";
const DesktopBannerImage =
  "https://ss-leagues.s3.amazonaws.com/NCAA/Oregon%20Ducks/2026%20Student%20Duck%20Sports%20Pass%20Attend%20Image%20Desktop.jpg";
const MobileBannerImage =
  "https://ss-leagues.s3.amazonaws.com/NCAA/Oregon%20Ducks/2026%20Student%20Duck%20Sports%20Pass%20Attend%20Image%20Mobile.jpg";

const TEAM_SCHEMA = "oregon_marketplace_sandbox";

export const oregon_marketplace_sandbox: TTeamConfig = {
  TEAM_SCHEMA: "oregon_marketplace_sandbox",
  TEAM_FULL_NAME: "Oregon Ducks",
  BROWSER_TITLE: "Oregon Student Tickets",
  CLIENT_ID: "97787f339f16.seasonshare.oregonducks-oregonducks.us",
  VENDOR: constants.vendorUUIDs.ticketmaster,
  MARKETPLACE_VENDOR: constants.vendorUUIDs.stripe,

  TEAM_GLOBAL_SETTINGS: {
    ACCOUNT_MANAGER_URL: "https://am.ticketmaster.com/oregonducks/",
    COLORS_PRIMARY: "#154733",
    COLORS_SECONDARY: "#FEE123",
    DEFAULT_APP_ROUTE: routes.home,
    DEFAULT_APP_ROUTE_AFTER_LOGIN: routes.home,
    IS_MARKETPLACE_ENABLED: true,
    LOGOUT_ON_INVALID_SESSION: false,
    DISABLE_SIT_WITH_FRIENDS: true,
    DISABLE_PASS_ON_ACCOUNT_RESTRICTION: true,

    NAV_MENU: {
      HIDE_EARN_REWARDS: true,
      HIDE_MANAGE_SUBSCRIPTION: true,
    },

    FOOTER: {
      LINKS: FOOTER_LINKS,
      BACKGROUND_COLOR: "#154733",
    },

    IS_INTERCOM_ENABLED: true,
    IS_CROWD_HANDLER_ENABLED: true,
  },

  LANDING_PAGE_SETTINGS: {
    PASS_BANNER: {
      PASS_DISPLAY_MODE: "separate",
      IS_FLUID_BACKGROUND_IMAGE_SIZE: true,
      DISABLE_BACKGROUND_IMAGE_FILTER: true,
    },

    HIDE_PASSES: true,
    HIDE_UPCOMING_EVENTS: true,

    REGISTRATION_CARD_SETTINGS: {
      REGISTRATION_CARDS,
      HIDE_SECTION_TITLE: true,
    },

    PASS: {
      HIDE_DECIMAL_POINTS_AFTER_PRICE: true,
      SUBSCRIBE_BUTTON_TEXT: "Purchase Now",
    },

    STATIC_BANNER: {
      IS_ENABLED: true,
      IS_FLUID_BACKGROUND_IMAGE_SIZE: true,
    },

    SHOW_ALL_EVENTS_UNDER_PASS: true,
  },

  HOME_PAGE_SETTINGS: {
    EVENT_CARD: {
      SHOW_SINGLE_GAMES_IN_COLLAPSE: false,
      ATTEND_BUTTON_LABEL: "Request",
      HIDE_TICKET_DELIVERY_STATUS: false,
      HIDE_SEAT_COUNT_MAPPING: { included_with_pass: true },
      CHECK_IN_LABEL: "Request Period",
    },

    ATTEND_EVENT_MODAL: {
      TITLE: "Manage Request",
      DESCRIPTION:
        "By requesting a ticket, you are not guaranteed once for the upcoming game. You will be notified on Monday if you receive a ticket with a link to manage your barcode.",
      ATTEND_BUTTON_LABEL: "Request Ticket",
      ATTEND_BUTTON_ATTENDING_LABEL: "Requested",
      ATTEND_BUTTON_NOT_ATTENDING_LABEL: "Not Requested",
    },

    ADD_ON: {
      CHECKOUT_BUTTON_LABEL: "Claim Tickets",
      ADD_ON_PURCHASE_SUCCESS_MESSAGE,
    },
  },

  GROUP_INVITE_PAGE_SETTINGS: {
    PASS_BANNER: {
      DISABLE_BACKGROUND_IMAGE_FILTER: true,
      PASS_DISPLAY_MODE: "separate",
    },
  },

  CHECKOUT_PAGE_SETTINGS: {
    CHECKOUT_FORM: {
      SAVE_CARD_SWITCH_ENABLED: false,
      DISABLED_PAYMENT_METHODS: ["AE"],
    },

    ADD_DARK_OVERLAY: true,
    ADD_ONS_LABEL: "Tickets",
  },

  MANAGE_SUBSCRIPTION_PAGE_SETTINGS: {
    ZERO_STATE: {
      OPT_OUT_EARLIEST_DATE: new Date("10/01/2024"),
    },
  },

  MARKETPLACE_GLOBAL_SETTINGS: {
    GA_LABEL: "Student General Admission",

    BUYER_HOW_IT_WORKS,
    SELLER_HOW_IT_WORKS,
    FAQS: MARKETPLACE_FAQS(TEAM_SCHEMA),
    MARKETPLACE_REDIRECT_WARN_MODAL_DESCRIPTION:
      "You're entering a peer-to-peer marketplace where students can buy and sell tickets directly with each other. Prices are set by fellow students and are not regulated or controlled by the University of Oregon.",
    STRIPE_KYC_CONFIRMATION_MODAL_DESCRIPTION:
      "The University of Oregon and Attend use Stripe as a secure payment processor. You're about to leave this page to complete seller onboarding, where we'll collect your name, address, phone number, and bank details so we can deposit your payout once your ticket sells.",

    LISTING_CHECKOUT_TERMS_AND_CONDITION: <ListingCheckoutTermsAndCondition />,
    CREATE_LISTING_TERMS_AND_CONDITION: <CreateListingTermsAndCondition />,
  },

  MARKETPLACE_HOME_PAGE_SETTINGS: {
    STATIC_BANNER: {
      IS_ENABLED: true,
      IS_FLUID_BACKGROUND_IMAGE_SIZE: false,
    },

    BECOME_SELLER_CARD: {
      TITLE: "Sell Your Football Student Ticket",
      DESCRIPTION:
        "Got a student ticket you can't use? Sell it to another student and make sure your seat is filled with school spirit. List your ticket in seconds and enjoy a quick, secure process that turns your unused ticket into extra cash.",
      BUTTON_TEXT: "Sell Your Ticket",
    },

    BECOME_BUYER_CARD: {
      TITLE: "Buy a Student Football Ticket",
      DESCRIPTION:
        "Looking for a ticket to the big game or event? Browse listings from fellow students and grab your seat in the student section. Enjoy a safe, hassle-free purchase and join your classmates in the action.",
      BUTTON_TEXT: "Browse Listings",
    },

    SELLER_HOW_IT_WORKS_TITLE: "How to Sell Your Football Student Ticket",
    BUYER_HOW_IT_WORKS_TITLE: "How to Buy a Student Football Ticket",
  },

  MARKETPLACE_EVENTS_PAGE_SETTINGS: {
    PAGE_INFO_TEXT: MARKETPLACE_EVENTS_PAGE_INFO_TEXT,
    CART: {
      MAX_LISTINGS_COUNT: 4,
    },
  },

  MARKETPLACE_CREATE_LISTING_PAGE_SETTINGS: {
    PAGE_INFO_TEXT: MARKETPLACE_CREATE_LISTING_PAGE_INFO_TEXT,
  },

  IMAGES: {
    LOGO_URL,
    LANDING_PAGE_STATIC_BANNER_URL: DesktopBannerImage,
    ORDER_SUMMERY_BACKGROUND_URL: OrderSummaryDesktopImage,
    MOBILE_LANDING_PAGE_STATIC_BANNER_URL: MobileBannerImage,
    GROUP_INVITE_PAGE_STATIC_BANNER_URL: GroupInviteBanner.src,
    MARKETPLACE_HOME_PAGE_STATIC_BANNER_URL: DesktopBannerImage,
  },

  LANGUAGES: {
    FAQS,
    TERMS_AND_CONDITION,
  },
};
